import type { ValueOrUpdater } from '@dresscode-frontend/utility-types';
import { createFileRoute, getRouteApi, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useEffect } from 'react';
import { FillScreen, HStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { getOperationPackageInstance } from '../../-query/api';
import { isInHouseSelfReportPackageType } from '../../-utils/inHousePackageUtil';
import { isPackageWithoutDataTab } from '../../-utils/isPackageWithoutDataTab';
import { useListOperationTaskInstances } from './-components/BulkAppUserOperation/-query';
import { usePackage } from './-hooks/usePackage';
import { SearchSchema } from './-zod-schemas';
import type { SearchParams } from './-zod-schemas';
export const Route = createFileRoute('/_staff/it-operation-core/_detail/operation-instances/$instanceId/_layout/data/')({
  beforeLoad: async ({
    params
  }) => {
    const instance = await getOperationPackageInstance({
      instanceId: params.instanceId
    });
    if (isPackageWithoutDataTab(instance.packageType)) {
      throw redirect({
        to: '/it-operation-core/operation-instances/$instanceId/detail',
        params: {
          instanceId: params.instanceId
        }
      });
    }
  },
  component: RouteComponent,
  validateSearch: zodValidator(SearchSchema)
});
const parentRoute = getRouteApi('/_staff/it-operation-core/_detail/operation-instances/$instanceId/_layout');
function isInHouseSelfReportDataTab(packageType: ReturnType<typeof parentRoute.useLoaderData>['packageType']) {
  return isInHouseSelfReportPackageType(packageType);
}
function RouteComponent() {
  const {
    id,
    packageType
  } = parentRoute.useLoaderData();
  const {
    Component
  } = usePackage({
    packageType
  });
  const skipTaskInstanceBootstrap = isInHouseSelfReportDataTab(packageType);
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();

  // タスクインスタンスを取得
  const {
    data: taskInstances
  } = useListOperationTaskInstances(id, {
    enabled: !skipTaskInstanceBootstrap
  });
  const onSearch = useCallback((query: ValueOrUpdater<Partial<SearchParams>>) => {
    navigate({
      search: prev => {
        const newQuery = typeof query === 'function' ? query(prev) : query;
        return {
          ...prev,
          ...newQuery
        };
      }
    });
  }, [navigate]);

  // 初回アクセス時にoperationTaskInstanceIdが未設定の場合、デフォルト値を設定
  useEffect(() => {
    if (skipTaskInstanceBootstrap) {
      return;
    }
    if (!searchParams.operationTaskInstanceId && taskInstances && taskInstances.length > 0) {
      // InProgressタスクを優先、なければ最初のタスク
      const inProgressTask = taskInstances.find(task => task.status === 'InProgress');
      const defaultTaskId = inProgressTask?.taskInstanceId || taskInstances[0]?.taskInstanceId;
      if (defaultTaskId) {
        navigate({
          search: prev => ({
            ...prev,
            operationTaskInstanceId: defaultTaskId
          }),
          replace: true // 履歴を置き換える
        });
      }
    }
  }, [skipTaskInstanceBootstrap, searchParams.operationTaskInstanceId, taskInstances, navigate]);
  if (!skipTaskInstanceBootstrap && !searchParams.operationTaskInstanceId && !taskInstances) {
    return <ViewportLoader />;
  }
  return <FillScreen className="min-w-0">
      <HStack className="h-full min-w-0 overflow-hidden">
        <Component instanceId={id} searchParams={searchParams} onSearch={onSearch} />
      </HStack>
    </FillScreen>;
}